import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareText, Phone, Route } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { RfqAssistant } from "@/components/RfqAssistant";
import { TrackingInteractive } from "@/pages/Tracking";
import { CookieConsent } from "@/components/CookieConsent";

const PHONE = "+60166161199";
const WHATSAPP = "https://wa.me/60166161199";
const PATH_SERVICES = { "/cold-room-warehousing": "warehousing", "/ecommerce-fulfilment": "fulfilment", "/refrigerated-transport": "transport", "/distribution": "distribution", "/cross-border-logistics": "cross-border", "/4pl-solutions": "4pl" };
const OPENING = { warehousing: "I understand you are looking for storage. Let’s begin with the type of goods and required temperature.", transport: "I understand you need a delivery arranged. Let’s begin with the pickup and destination.", fulfilment: "I understand you are exploring fulfilment support. Let’s begin with your products and selling channels.", distribution: "I understand you need goods distributed to several locations. Let’s begin with the collection point and number of destinations.", "cross-border": "I understand you are planning a cross-border movement. Let’s begin with the origin and destination countries.", "4pl": "We’ll begin by understanding the providers, locations and handovers you currently manage, then identify where one coordination layer could help." };
function trackConversion(event, method) { const params = new URLSearchParams(window.location.search); window.dispatchEvent(new CustomEvent("ac-analytics", { detail: { event, method, landing_page: window.location.pathname, utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"), utm_campaign: params.get("utm_campaign") } })); }
function useFloatingChatVisibility(pathname) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(false);
    const selector = pathname === "/" ? ".hero-cta-group" : ".page-hero .hero-actions";
    const primary = document.querySelector(selector);
    const fallback = document.querySelector("main section");
    const trigger = primary || fallback;
    if (!trigger || !("IntersectionObserver" in window)) return undefined;
    let lastVisible = true;
    const observer = new IntersectionObserver(([entry]) => {
      const triggerVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
      if (triggerVisible === lastVisible) return;
      const floating = document.querySelector(".floating-chat");
      if (triggerVisible && floating?.contains(document.activeElement)) document.activeElement.blur();
      lastVisible = triggerVisible;
      setVisible(!triggerVisible);
    }, { threshold: [0.1, 0.2, 0.35], rootMargin: "0px 0px -48px 0px" });
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [pathname]);
  return visible;
}

export function PublicEnhancements() {
  const [assistant, setAssistant] = useState({ open: false, service: null, context: "" });
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const { pathname } = useLocation();
  const floatingVisible = useFloatingChatVisibility(pathname);
  const openAssistant = useCallback((service, context) => setAssistant({ open: true, service: service || null, context: OPENING[service] || context || window.location.pathname }), []);
  const closeAssistant = useCallback(() => setAssistant((current) => ({ ...current, open: false })), []);
  useEffect(() => {
    const handler = (event) => { const cookieSettings = event.target.closest("[data-cookie-settings]"); if (cookieSettings) { event.preventDefault(); document.dispatchEvent(new CustomEvent("open-cookie-settings")); return; } const language = event.target.closest("[data-policy-language]"); if (language) { const selected = language.dataset.policyLanguage; document.querySelectorAll("[data-policy-panel]").forEach((panel) => { panel.hidden = panel.dataset.policyPanel !== selected; }); document.querySelectorAll("[data-policy-toc]").forEach((toc) => { toc.hidden = toc.dataset.policyToc !== selected; }); document.querySelectorAll("[data-policy-language]").forEach((button) => { const active = button.dataset.policyLanguage === selected; button.classList.toggle("active", active); button.setAttribute("aria-pressed", String(active)); }); return; } const rfq = event.target.closest("[data-open-rfq]"); if (rfq) { event.preventDefault(); const service = rfq.dataset.service || PATH_SERVICES[window.location.pathname] || null; openAssistant(service, rfq.dataset.context); return; } const conversion = event.target.closest("[data-conversion]"); if (conversion) trackConversion(conversion.dataset.conversion === "whatsapp" ? "whatsapp_handover" : "telephone_click", conversion.dataset.conversion); };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [openAssistant]);
  useEffect(() => {
    const details = document.querySelector("details.ssr-mobile-menu");
    if (!details) return undefined;
    const summary = details.querySelector("summary");
    const onToggle = () => {
      document.body.classList.toggle("mobile-menu-open", details.open);
      summary?.setAttribute("aria-expanded", String(details.open));
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape" || !details.open) return;
      details.open = false;
      onToggle();
      summary?.focus();
    };
    const onClick = (event) => {
      if (!event.target.closest("a")) return;
      details.open = false;
      onToggle();
      summary?.focus();
    };
    summary?.setAttribute("aria-expanded", String(details.open));
    details.addEventListener("toggle", onToggle);
    details.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      details.removeEventListener("toggle", onToggle);
      details.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("mobile-menu-open");
    };
  }, [pathname]);
  useEffect(() => {
    const options = [...document.querySelectorAll("[data-builder-option]")];
    const desktopResult = document.querySelector(".builder-result-desktop");
    if (!options.length || !desktopResult) return undefined;
    const select = (button) => {
      options.forEach((option) => {
        const active = option === button;
        option.classList.toggle("selected", active);
        option.setAttribute("aria-pressed", String(active));
        const mobileResult = option.parentElement?.querySelector(".builder-result-mobile");
        if (mobileResult) mobileResult.hidden = !active;
      });
      desktopResult.querySelector("h3").textContent = button.dataset.resultTitle;
      desktopResult.querySelector("p").textContent = button.dataset.resultCopy;
      const cta = desktopResult.querySelector("[data-open-rfq]");
      if (cta) cta.dataset.context = button.dataset.resultTitle;
    };
    const listeners = options.map((button) => {
      const handler = () => select(button);
      button.addEventListener("click", handler);
      return [button, handler];
    });
    return () => listeners.forEach(([button, handler]) => button.removeEventListener("click", handler));
  }, [pathname]);
  useEffect(() => {
    const cta = document.querySelector(".hero-cta-group");
    if (window.innerWidth > 800) { setShowMobileActions(false); return undefined; }
    if (!cta) { setShowMobileActions(true); return undefined; }
    if (!("IntersectionObserver" in window)) { setShowMobileActions(false); return undefined; }
    let lastVisible = true;
    const observer = new IntersectionObserver(([entry]) => { const visible = entry.isIntersecting && entry.intersectionRatio >= 0.35; if (visible !== lastVisible) { lastVisible = visible; setShowMobileActions(!visible); } }, { threshold: [0.2, 0.35, 0.5], rootMargin: "0px 0px -72px 0px" });
    observer.observe(cta);
    return () => observer.disconnect();
  }, [pathname]);
  useEffect(() => {
    const sentinel = document.querySelector(".footer-sentinel");
    if (!sentinel || !("IntersectionObserver" in window)) return undefined;
    let lastState = false;
    const observer = new IntersectionObserver(([entry]) => { const nextState = entry.isIntersecting; if (nextState === lastState) return; const mobileBar = document.querySelector(".mobile-actions"); if (nextState && mobileBar?.contains(document.activeElement)) return; lastState = nextState; document.body.classList.toggle("footer-visible", nextState); setFooterVisible(nextState); }, { threshold: [0, 0.1], rootMargin: "0px 0px 40px 0px" });
    observer.observe(sentinel);
    return () => { observer.disconnect(); document.body.classList.remove("footer-visible"); };
  }, [pathname]);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const update = () => document.body.classList.toggle("keyboard-open", window.innerHeight - viewport.height > 150);
    update();
    viewport.addEventListener("resize", update);
    return () => {
      viewport.removeEventListener("resize", update);
      document.body.classList.remove("keyboard-open");
    };
  }, []);
  const mobileVisible = showMobileActions && !footerVisible && !assistant.open;
  const floatingShown = floatingVisible && !assistant.open;
  return <><button className={`floating-chat ${floatingShown ? "is-shown" : "is-concealed"} ${footerVisible ? "above-footer" : ""}`} aria-hidden={!floatingShown} tabIndex={floatingShown ? 0 : -1} onClick={() => openAssistant(PATH_SERVICES[window.location.pathname], window.location.pathname)} aria-label="Start an enquiry"><MessageSquareText/><span>Start an enquiry</span></button><nav className={`mobile-actions ${mobileVisible ? "is-visible" : "is-hidden"}`} aria-hidden={!mobileVisible} aria-label="Quick actions"><button tabIndex={mobileVisible ? 0 : -1} onClick={() => openAssistant(null, "mobile")} aria-label="Start an enquiry"><MessageSquareText/><span>Enquire</span></button><a tabIndex={mobileVisible ? 0 : -1} href={`tel:${PHONE}`} aria-label="Call Abang Cold" data-conversion="telephone"><Phone/><span>Call Us</span></a><a tabIndex={mobileVisible ? 0 : -1} href={WHATSAPP} aria-label="Chat with Abang Cold on WhatsApp" data-conversion="whatsapp"><FaWhatsapp className="whatsapp-icon" aria-hidden="true"/><span>WhatsApp</span></a><a tabIndex={mobileVisible ? 0 : -1} href="/track"><Route/><span>Track</span></a></nav>{window.location.pathname === "/track" && <TrackingInteractive/>}{assistant.open && <RfqAssistant open={assistant.open} initialService={assistant.service} context={assistant.context} onClose={closeAssistant}/>}<CookieConsent/></>;
}
